import dayjs from "dayjs";

import { OPFSStorage } from "@/Services/OPFSStorage";
import { parseUploadedUrl } from "@/Services/UploadPhoto";

import {
    StoredPhoto,
    StoredPhotoMeta,
    StoredPhotoSlot,
} from "../PhotoStorage.types";

interface StoredPhotoMetaFs extends StoredPhotoMeta {
    fileName: string;
}

const PHOTOS_DIR = "photos";
const SLOTS_DIR = "slots";
const META_EXT = ".json";

const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/heic": "heic",
    "image/heif": "heif",
};

const encodeKey = (key: string): string =>
    encodeURIComponent(key).replace(/\./g, "%2E");

const getFileExtension = (file: File): string => {
    const nameParts = file.name.split(".");
    const extFromName =
        nameParts.length > 1 ? nameParts[nameParts.length - 1]?.trim() : "";

    if (extFromName) return extFromName.toLowerCase();

    return MIME_TYPE_TO_EXTENSION[file.type] ?? "bin";
};

class PhotoStorageFs extends OPFSStorage {
    private async photosDir() {
        return super.getDir(PHOTOS_DIR);
    }

    private async slotsDir() {
        return super.getDir(SLOTS_DIR);
    }

    private encodePhotoIdForFs(id: string): string {
        return encodeURIComponent(id).replace(/\./g, "%2E");
    }

    private getPhotoMetaFileName(id: string): string {
        return `${this.encodePhotoIdForFs(id)}${META_EXT}`;
    }

    private getPhotoBinaryFileName(id: string, ext: string): string {
        return `${this.encodePhotoIdForFs(id)}.${ext}`;
    }

    private async getPhotoMeta(id: string): Promise<StoredPhotoMetaFs | null> {
        const dir = await this.photosDir();
        return this.readJson<StoredPhotoMetaFs>(
            dir,
            this.getPhotoMetaFileName(id),
        );
    }

    private async blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(blob);
        });
    }

    async savePhoto({
        id,
        file,
        fromSlot,
        skipUpload,
    }: {
        id: string;
        file: File;
        date: string;
        fromSlot: boolean;
        skipUpload?: boolean;
    }): Promise<void> {
        const dir = await this.photosDir();
        const ext = getFileExtension(file);
        const binaryFileName = this.getPhotoBinaryFileName(id, ext);

        await this.writeBinary(dir, binaryFileName, file);

        const photo: StoredPhotoMetaFs = {
            id,
            fileName: binaryFileName,
            name: file.name,
            type: file.type,
            size: file.size,
            taskComplete: false,
            uploadAttempts: 0,
            uploaded: false,
            fromSlot,
            skipUpload,
            createdAt: dayjs().toISOString(),
        };

        await this.writeJson(dir, this.getPhotoMetaFileName(id), photo);
    }

    async getPhoto(id: string): Promise<string | null> {
        const dir = await this.photosDir();
        const photo = await this.getPhotoMeta(id);
        if (!photo) return null;

        const file = await this.readBinary(dir, photo.fileName);
        if (!file) return null;

        return this.blobToBase64(file);
    }

    async getPhotoFile(id: string): Promise<File | null> {
        const dir = await this.photosDir();
        const photo = await this.getPhotoMeta(id);
        if (!photo) return null;

        return this.readBinary(dir, photo.fileName);
    }

    async getPhotoBlob(id: string): Promise<Blob | null> {
        return this.getPhotoFile(id);
    }

    async getAllPhotosMeta(): Promise<StoredPhotoMeta[]> {
        const dir = await this.photosDir();
        const metaList = await this.listJsonEntries<StoredPhotoMetaFs>(dir);

        return metaList.sort(
            (a, b) =>
                dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
        );
    }

    async getPhotosByIds(ids: string[]): Promise<StoredPhoto[]> {
        const dir = await this.photosDir();

        const photos = await Promise.all(
            ids.map(async (id) => {
                const meta = await this.getPhotoMeta(id);
                if (!meta) return null;

                const file = await this.readBinary(dir, meta.fileName);
                const base64 = file ? await this.blobToBase64(file) : "";

                const { fileName: _, ...rest } = meta;
                return { ...rest, base64 };
            }),
        );

        return photos
            .filter((p): p is StoredPhoto => p !== null)
            .sort(
                (a, b) =>
                    dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
            );
    }

    async deletePhoto(id: string): Promise<void> {
        const dir = await this.photosDir();
        const photo = await this.getPhotoMeta(id);

        await this.deleteFile(dir, this.getPhotoMetaFileName(id));

        if (photo?.fileName) {
            await this.deleteFile(dir, photo.fileName);
        }
    }

    async deleteAllPhotos(): Promise<void> {
        const root = await this.getRoot();

        try {
            await root.removeEntry(PHOTOS_DIR, { recursive: true });
        } catch {
            // ignore if directory does not exist
        }
    }

    async deletePhotosByIds(ids: string[]): Promise<void> {
        await Promise.all(ids.map((id) => this.deletePhoto(id)));
    }

    async photoExists(id: string): Promise<boolean> {
        const dir = await this.photosDir();

        try {
            await dir.getFileHandle(this.getPhotoMetaFileName(id));
            return true;
        } catch {
            return false;
        }
    }

    async getPhotoInfo(id: string): Promise<StoredPhotoMeta | null> {
        return this.getPhotoMeta(id);
    }

    async updatePhotoInfo(
        id: string,
        patch: Partial<Omit<StoredPhotoMeta, "id">>,
    ): Promise<void> {
        const dir = await this.photosDir();
        const existing = await this.getPhotoMeta(id);

        if (!existing) {
            throw new Error(`Photo with id ${id} not found`);
        }

        const updated: StoredPhotoMetaFs = {
            ...existing,
            ...patch,
            id: existing.id,
            fileName: existing.fileName,
        };

        await this.writeJson(dir, this.getPhotoMetaFileName(id), updated);
    }

    async getSlotAndDelete(pointId: string): Promise<StoredPhotoSlot | null> {
        const dir = await this.slotsDir();
        const slots = await this.listJsonEntries<StoredPhotoSlot>(dir);
        const slot = slots.find((item) => item.pointId === pointId) ?? null;

        if (slot) {
            await this.deleteFile(dir, `${encodeKey(slot.url)}${META_EXT}`);
        }

        return slot;
    }

    async createSlot({ url }: { url: string }): Promise<void> {
        const parseData = parseUploadedUrl(url);
        if (!parseData) return;

        const { pointId, middlePath, filename } = parseData;
        const dir = await this.slotsDir();

        await this.writeJson(dir, `${encodeKey(url)}${META_EXT}`, {
            url,
            pointId,
            filename,
            middlePath,
        });
    }

    async deleteSlot(url: string): Promise<void> {
        const dir = await this.slotsDir();
        await this.deleteFile(dir, `${encodeKey(url)}${META_EXT}`);
    }

    async getKind(): Promise<"opfs"> {
        return "opfs";
    }
}

export const photoStorageFs = new PhotoStorageFs();
export { PhotoStorageFs };
